import { FormatType, HeaderPositionType, TextStyleType } from "../constants";
import { formatter } from "../formatter";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class RadialBarChartCategory extends BaseCategory {
    get name() {
        return "Radial Bar Chart";
    }

    get type() {
        return SlideCategories.RADIAL_BAR_CHART;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for comparing multiple numbers, currencies, or percentages in a circular/radial bar visualization. Best for up to 8 items.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Provide data for a radial bar chart that compares multiple values in a circular format.
        Include a short label and numeric value for each item.
        Use format as number, percent/percentage, or currency. If currency, set currencyFormat (USD, EUR, GBP, JPY).`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            format: "number | percent | percentage | currency",
            currencyFormat: "none | USD | EUR | GBP | JPY (only if format is currency)",
            items: [{
                label: "string",
                value: "number"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return model.items.every(item => item && !isNaN(parseFloat(item.value)));
    }

    getSlideModels(model, options = {}) {
        const normalizedModel = this.normalizeModel(model);
        const radialInput = {
            ...normalizedModel,
            items: (normalizedModel.items || []).slice(0, 8)
        };

        return [
            this.buildRadialBarChart(radialInput, options)
        ].filter(m => m != null);
    }

    normalizeModel(model) {
        const format = this.normalizeFormat(model?.format);
        const items = (model?.items || [])
            .map(item => ({
                label: item?.label ?? "",
                value: parseFloat(item?.value)
            }))
            .filter(item => !isNaN(item.value));

        let normalizedItems = items;
        if (format === FormatType.PERCENT) {
            const looksFractional = items.length > 0 && items.every(item => Math.abs(item.value) <= 1);
            if (looksFractional) {
                normalizedItems = items.map(item => ({
                    ...item,
                    value: item.value * 100
                }));
            }
        }

        return {
            ...model,
            format,
            items: normalizedItems
        };
    }

    normalizeFormat(format) {
        const lower = `${format || ""}`.trim().toLowerCase();
        if (lower === "percent" || lower === "percentage") {
            return FormatType.PERCENT;
        }
        if (lower === "currency") {
            return FormatType.CURRENCY;
        }
        if (lower === "number") {
            return FormatType.NUMBER;
        }
        return FormatType.NUMBER;
    }

    getFormatOptions(model) {
        const options = {
            abbreviated: "lower"
        };
        if (model.format === FormatType.CURRENCY) {
            options.currency = formatter.parseCurrencySymbol(model.currencyFormat || "USD");
        }
        return options;
    }

    buildRadialBarChart(model, options = {}) {
        if (!model?.items?.length || model.items.length > 8) {
            return null;
        }

        let builder = new SlideModelBuilder("radial_bar_chart", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const values = model.items.map(item => item.value);
        const maxValue = model.format === FormatType.PERCENT
            ? Math.max(100, ...values)
            : Math.max(1, ...values);

        builder.createPrimary({
            collectionColor: "theme-to-light",
            trackStyle: "bar",
            startPosition: "top",
            minValue: 0,
            maxValue,
            format: model.format,
            formatOptions: this.getFormatOptions(model),
            items: model.items.map(item => ({
                value: item.value,
                label: builder.createSingleBlockText(item.label, TextStyleType.BODY)
            })),
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }
        return builder.model;
    }
}
